import { TableHeader } from "@tiptap/extension-table-header";

export const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("class"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.class) return {};
          return { class: attributes.class as string };
        },
      },
      style: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("style"),
        renderHTML: (attributes: Record<string, unknown>) => {
          const styles: string[] = [];

          if (attributes.backgroundColor) {
            styles.push(`background-color: ${attributes.backgroundColor}`);
          }
          if (attributes.textColor) {
            styles.push(`color: ${attributes.textColor}`);
          }
          if (attributes.borderColor) {
            styles.push(`border-color: ${attributes.borderColor}`);
          }
          if (attributes.borderWidth) {
            // borderWidth can be a number (single value) or string (4-value like "0px 1px 1px 0px")
            const bw = attributes.borderWidth;
            if (typeof bw === 'string') {
              styles.push(`border-width: ${bw}`);
            } else {
              styles.push(`border-width: ${bw}px`);
            }
          }
          if (attributes.textAlign) {
            styles.push(`text-align: ${attributes.textAlign}`);
          }
          if (attributes.verticalAlign) {
            styles.push(`vertical-align: ${attributes.verticalAlign}`);
          }
          if (attributes.fontSize) {
            styles.push(`font-size: ${attributes.fontSize}px`);
          }
          if (attributes.fontWeight) {
            styles.push(`font-weight: ${attributes.fontWeight}`);
          }
          if (attributes.padding) {
            styles.push(`padding: ${attributes.padding}`);
          }
          if (attributes.width) {
            styles.push(`width: ${attributes.width}`);
          }
          if (attributes.minWidth) {
            styles.push(`min-width: ${attributes.minWidth}`);
          }

          if (attributes.style && typeof attributes.style === "string") {
            const existingStyles = attributes.style.split(";").filter(Boolean);
            const handledProps = [
              "background-color", "color", "border-color", "border-width",
              "text-align", "vertical-align", "font-size", "font-weight", 
              "padding", "width", "min-width"
            ];
            existingStyles.forEach((s: string) => {
              const prop = s.split(":")[0]?.trim().toLowerCase();
              if (prop && !handledProps.includes(prop)) {
                styles.push(s.trim());
              }
            });
          }

          if (styles.length === 0) return {};
          return { style: styles.join("; ") };
        },
      },
      "data-responsive-styles": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-responsive-styles"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-responsive-styles"]) return {};
          return { "data-responsive-styles": attributes["data-responsive-styles"] as string };
        },
      },
      "data-column-index": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-column-index"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-column-index"]) return {};
          return { "data-column-index": attributes["data-column-index"] as string };
        },
      },
      // Per-breakpoint column width settings (type:value format, e.g., "percent:25" or "pixel:200" or "auto:")
      "data-col-width-xs": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-col-width-xs"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-col-width-xs"]) return {};
          return { "data-col-width-xs": attributes["data-col-width-xs"] as string };
        },
      },
      "data-col-width-sm": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-col-width-sm"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-col-width-sm"]) return {};
          return { "data-col-width-sm": attributes["data-col-width-sm"] as string };
        },
      },
      "data-col-width-md": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-col-width-md"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-col-width-md"]) return {};
          return { "data-col-width-md": attributes["data-col-width-md"] as string };
        },
      },
      "data-col-width-lg": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-col-width-lg"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-col-width-lg"]) return {};
          return { "data-col-width-lg": attributes["data-col-width-lg"] as string };
        },
      },
      "data-col-width-xl": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-col-width-xl"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-col-width-xl"]) return {};
          return { "data-col-width-xl": attributes["data-col-width-xl"] as string };
        },
      },
      "data-column-widths": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-column-widths"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-column-widths"]) return {};
          return { "data-column-widths": attributes["data-column-widths"] as string };
        },
      },
      "data-column-settings": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-column-settings"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-column-settings"]) return {};
          return { "data-column-settings": attributes["data-column-settings"] as string };
        },
      },
      "data-cell-id": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-cell-id"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-cell-id"]) return {};
          return { "data-cell-id": attributes["data-cell-id"] as string };
        },
      },
      "data-cell-settings": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-cell-settings"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes["data-cell-settings"]) return {};
          return { "data-cell-settings": attributes["data-cell-settings"] as string };
        },
      },
      backgroundColor: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.backgroundColor || null,
        renderHTML: () => ({}),
      },
      textColor: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.color || null,
        renderHTML: () => ({}),
      },
      borderColor: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.borderColor || null,
        renderHTML: () => ({}),
      },
      borderWidth: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const borderWidth = element.style.borderWidth;
          if (!borderWidth) return null;
          // Preserve multi-value border-width as string, single value as number
          return borderWidth.includes(' ') ? borderWidth : parseInt(borderWidth);
        },
        renderHTML: () => ({}),
      },
      textAlign: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.textAlign || null,
        renderHTML: () => ({}),
      },
      verticalAlign: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.verticalAlign || null,
        renderHTML: () => ({}),
      },
      fontSize: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const fontSize = element.style.fontSize;
          return fontSize ? parseInt(fontSize) : null;
        },
        renderHTML: () => ({}),
      },
      fontWeight: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.fontWeight || null,
        renderHTML: () => ({}),
      },
      padding: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.padding || null,
        renderHTML: () => ({}),
      },
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.width || null,
        renderHTML: () => ({}),
      },
      minWidth: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.minWidth || null,
        renderHTML: () => ({}),
      },
    };
  },
});


